// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The login bridge between Supabase and ruxd.
 *
 * Supabase owns the account; ruxd owns the session the app runs on. After a
 * Supabase sign-in the access token goes to `POST /api/v1/auth/session`,
 * which answers with ruxd's HttpOnly cookie — confirmed with `GET /auth/me`
 * before anyone navigates, so a cookie the browser dropped (plain http, a
 * blocked cookie) is an error on screen, not a reload loop. From then on:
 *  - a 401 from ruxd re-bridges (`unauthorized.ts`), refreshing the Supabase
 *    token first if ruxd refuses the current one;
 *  - `TOKEN_REFRESHED` re-bridges once ruxd's session ends within 2 h
 *    (`session.expires_at`, shared across tabs);
 *  - `SIGNED_OUT` (also from another tab) ends the ruxd session;
 *  - "Log ud" ends both.
 *
 * Exchanges are rare on purpose: each one replaces the browser's ruxd
 * session (the old one lives on ~60 s). They are single-flight in a tab,
 * serialized across tabs (a Web Lock), and skipped when another exchange —
 * from any tab; the cookie is shared — finished after the failing request
 * started.
 *
 * Kept free of React and the DOM so it is unit-tested in Node with a mocked
 * Supabase client.
 */

import { ApiRequestError } from '../api/client';
import type { SupabaseAuth } from '../api/supabase';
import type { AuthMe } from '../api/types';

/** The ruxd calls the bridge makes (`AuthClient`). */
export interface BridgeApi {
  session(accessToken: string): Promise<AuthMe>;
  me(): Promise<AuthMe>;
  logout(): Promise<void>;
}

/** When the last exchange finished, shared by every tab; and a cross-tab lock. */
export interface BridgeCoordination {
  now(): number;
  lastExchange(): number;
  markExchange(at: number): void;
  /** When ruxd's session ends (ms), as last reported by ruxd; null when unknown. */
  sessionExpiry(): number | null;
  markSessionExpiry(at: number | null): void;
  /** Run `fn` while no other tab is exchanging. */
  exclusive<T>(fn: () => Promise<T>): Promise<T>;
}

/** ruxd refused a valid Supabase sign-in: unknown to ruxd, or banned. */
export class BridgeRefusedError extends Error {
  constructor(readonly reason: unknown) {
    super('ruxd refused the Supabase session');
    this.name = 'BridgeRefusedError';
  }
}

/** ruxd issued a session, but the browser did not keep the cookie. */
export class CookieNotStoredError extends Error {
  constructor() {
    super(
      'Login lykkedes, men browseren gemte ikke sessionen. Siden skal åbnes over HTTPS, og cookies skal være tilladt for den.',
    );
    this.name = 'CookieNotStoredError';
  }
}

/**
 * `TOKEN_REFRESHED` re-bridges when ruxd's session ends within this window
 * (`session.expires_at` from /auth/session or /auth/me). Supabase refreshes
 * about hourly, so one refresh always lands inside it before the 12 h cap.
 */
export const REFRESH_REBRIDGE_BEFORE_EXPIRY_MS = 2 * 60 * 60 * 1000;

/** Never two refresh re-bridges this close together, across all tabs. */
export const REFRESH_REBRIDGE_MIN_GAP_MS = 10 * 60 * 1000;

/** The fallback when ruxd does not report the expiry: re-bridge when the last exchange is this old. */
export const REFRESH_REBRIDGE_AFTER_MS = 6 * 60 * 60 * 1000;

/** `session.expires_at` from an /auth answer, in ms; null when absent or unreadable. */
export function sessionExpiryOf(me: AuthMe | null | undefined): number | null {
  const raw = me?.session?.expires_at;
  if (typeof raw !== 'string' || raw === '') return null;
  const at = Date.parse(raw);
  return Number.isFinite(at) ? at : null;
}

/** Whether a Supabase token refresh should also renew ruxd's session. */
export function shouldRebridgeOnRefresh(now: number, expiry: number | null, lastExchange: number): boolean {
  // Another tab's refresh just did it (they all fire about together).
  if (now - lastExchange < REFRESH_REBRIDGE_MIN_GAP_MS) return false;
  if (expiry !== null) return expiry - now < REFRESH_REBRIDGE_BEFORE_EXPIRY_MS;
  return now - lastExchange >= REFRESH_REBRIDGE_AFTER_MS;
}

const isUnauthorized = (cause: unknown) => cause instanceof ApiRequestError && cause.status === 401;

const LAST_EXCHANGE_KEY = 'reusex-bridge-last-exchange';
const SESSION_EXPIRY_KEY = 'reusex-bridge-session-expiry';

/** The browser's coordination: localStorage for the time, Web Locks for the lock. */
export function browserCoordination(): BridgeCoordination {
  let local = 0;
  let localExpiry: number | null = null;
  return {
    now: () => Date.now(),
    sessionExpiry() {
      try {
        const raw = window.localStorage.getItem(SESSION_EXPIRY_KEY);
        if (raw === null) return localExpiry;
        const at = Number(raw);
        return raw !== '' && Number.isFinite(at) ? at : null;
      } catch {
        return localExpiry;
      }
    },
    markSessionExpiry(at) {
      localExpiry = at;
      try {
        if (at === null) window.localStorage.removeItem(SESSION_EXPIRY_KEY);
        else window.localStorage.setItem(SESSION_EXPIRY_KEY, String(at));
      } catch {
        /* private mode: this tab still remembers */
      }
    },
    lastExchange() {
      try {
        return Math.max(local, Number(window.localStorage.getItem(LAST_EXCHANGE_KEY)) || 0);
      } catch {
        return local;
      }
    },
    markExchange(at) {
      local = at;
      try {
        window.localStorage.setItem(LAST_EXCHANGE_KEY, String(at));
      } catch {
        /* private mode: this tab still remembers */
      }
    },
    exclusive(fn) {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      return locks ? locks.request('reusex-bridge', fn) : fn();
    },
  };
}

/** One tab only, no lock: tests and a browser without Web Locks. */
export function memoryCoordination(clock: () => number = () => Date.now()): BridgeCoordination {
  let last = 0;
  let expiry: number | null = null;
  return {
    now: clock,
    sessionExpiry: () => expiry,
    markSessionExpiry: (at) => {
      expiry = at;
    },
    lastExchange: () => last,
    markExchange: (at) => {
      last = at;
    },
    exclusive: (fn) => fn(),
  };
}

export class SessionBridge {
  private pending: Promise<AuthMe | null> | null = null;
  private signingOut = false;

  constructor(
    private readonly auth: SupabaseAuth,
    private readonly api: BridgeApi,
    private readonly coord: BridgeCoordination = memoryCoordination(),
  ) {}

  /** The exchange, confirmed: rejects with CookieNotStoredError when /auth/me still says 401. */
  private async exchange(accessToken: string): Promise<AuthMe> {
    const issued = await this.api.session(accessToken);
    try {
      const me = await this.api.me();
      this.coord.markExchange(this.coord.now());
      this.coord.markSessionExpiry(sessionExpiryOf(me) ?? sessionExpiryOf(issued));
      return me;
    } catch (cause) {
      if (isUnauthorized(cause)) throw new CookieNotStoredError();
      throw cause;
    }
  }

  /**
   * Password sign-in, then the bridge. Rejects with a `SupabaseAuthError`
   * (wrong password, rate limit, …), a `BridgeRefusedError` — after which
   * the Supabase session is dropped again, so the browser is not half
   * signed in — or a `CookieNotStoredError`.
   */
  async signIn(email: string, password: string): Promise<AuthMe> {
    const session = await this.auth.signInWithPassword(email, password);
    try {
      return await this.coord.exclusive(() => this.exchange(session.access_token));
    } catch (cause) {
      if (!isUnauthorized(cause)) throw cause;
      await this.auth.signOut().catch(() => undefined);
      throw new BridgeRefusedError(cause);
    }
  }

  /** Mail a login link; it comes back to `redirectTo` (the login page). */
  sendLoginLink(email: string, redirectTo: string): Promise<void> {
    return this.auth.sendLoginLink(email, redirectTo);
  }

  /**
   * A fresh ruxd session from the stored Supabase one: who is signed in, or
   * null when there is no Supabase session or ruxd refuses it even after a
   * token refresh. Other failures (network, 5xx, CookieNotStoredError)
   * reject. Concurrent calls in a tab share one attempt.
   */
  rebridge(): Promise<AuthMe | null> {
    this.pending ??= this.coord.exclusive(() => this.attempt()).finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  /**
   * For a request that started at `since` and got a 401: true when ruxd
   * should now take it again — another exchange (this tab's or another's)
   * finished after it started, or this call made one.
   */
  async rebridgeFor(since: number): Promise<boolean> {
    if (this.coord.lastExchange() > since) return true;
    if (this.pending) return (await this.pending) !== null;
    this.pending ??= this.coord
      .exclusive(async () => {
        // Another tab may have exchanged while this one waited for the lock.
        if (this.coord.lastExchange() > since) return this.api.me().catch(() => null);
        return this.attempt();
      })
      .finally(() => {
        this.pending = null;
      });
    return (await this.pending) !== null;
  }

  private async attempt(): Promise<AuthMe | null> {
    const session = await this.auth.getSession();
    if (!session) return null;
    try {
      return await this.exchange(session.access_token);
    } catch (cause) {
      if (!isUnauthorized(cause)) throw cause;
    }
    // ruxd refused the token (it may have just expired): refresh once.
    const fresh = await this.auth.refreshSession();
    if (!fresh || fresh.access_token === session.access_token) return null;
    try {
      return await this.exchange(fresh.access_token);
    } catch (cause) {
      if (isUnauthorized(cause)) return null;
      throw cause;
    }
  }

  /**
   * Note the session expiry from any /auth/me answer (AuthGate's boot check),
   * so every tab knows when ruxd's session ends. Answers without it change
   * nothing.
   */
  observe(me: AuthMe): void {
    const at = sessionExpiryOf(me);
    if (at !== null) this.coord.markSessionExpiry(at);
  }

  /** Keep ruxd in step with Supabase; returns the unsubscribe. */
  listen(): () => void {
    return this.auth.onAuthStateChange((event) => {
      // Not awaited: supabase-js holds a lock while it runs listeners.
      if (event === 'TOKEN_REFRESHED') {
        // Supabase refreshes hourly, in every tab; ruxd's session slides on
        // its own. Renew it only when it ends within 2 h (its 12 h cap), so
        // the cap never lands as a 401; an older ruxd that does not report
        // the expiry falls back to the last exchange's age.
        const c = this.coord;
        if (!shouldRebridgeOnRefresh(c.now(), c.sessionExpiry(), c.lastExchange())) return;
        void this.rebridge().catch(() => undefined);
      } else if (event === 'SIGNED_OUT' && !this.signingOut) {
        void this.api.logout().catch(() => undefined);
      }
    });
  }

  /** "Log ud": ruxd first (it needs the cookie), then Supabase. */
  async signOut(): Promise<void> {
    this.signingOut = true;
    try {
      await this.api.logout().catch(() => undefined);
      this.coord.markSessionExpiry(null);
      await this.auth.signOut().catch(() => undefined);
    } finally {
      this.signingOut = false;
    }
  }
}
